import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import useAuthUser from '../services/useAuthUser';
import cognidoorBanner from '../assets/images/Congnidoor Banner.png';
import PaymentMethodAccordion from '../components/checkout/PaymentMethodAccordion';
import PayPalPaymentSection from '../components/checkout/PayPalPaymentSection';
import PaystackPaymentSection from '../components/checkout/PaystackPaymentSection';
import CheckoutTrustSection from '../components/checkout/CheckoutTrustSection';
import {
  getPlanById,
  getAmountUsd,
  isValidPlanId,
  isValidBillingTerm,
} from '../config/pricingLadder';
import { getAttributionSnapshot } from '../lib/attribution';
import { trackBeginCheckout } from '../lib/analytics/gtag';

const API_BASE = process.env.REACT_APP_API_URL || '';

function PaymentAccordionSkeleton() {
  return (
    <div className="space-y-3" aria-hidden>
      <div className="rounded-xl border border-slate-200 bg-white p-4 h-[96px] animate-pulse bg-slate-50" />
    </div>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuthUser();
  const [resolved, setResolved] = useState(null);
  const [paymentsConfig, setPaymentsConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [idToken, setIdToken] = useState('');
  const [orderId, setOrderId] = useState('');
  const [paystackDisplay, setPaystackDisplay] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [beginCheckoutTracked, setBeginCheckoutTracked] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      const redirect = `${location.pathname}${location.search}`;
      navigate(`/auth?redirect=${encodeURIComponent(redirect)}`, { replace: true });
    }
  }, [user, loading, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!user) return;
    user.getIdToken().then(setIdToken).catch(() => setIdToken(''));
  }, [user]);

  useEffect(() => {
    if (!API_BASE) {
      setConfigLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/payments/config`)
      .then((res) => (res.ok ? res.json() : null))
      .then((config) => {
        if (config) setPaymentsConfig(config);
      })
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    const state = location.state || {};
    const plan_id = state.plan_id ?? searchParams.get('plan_id');
    const billing_term = state.billing_term ?? searchParams.get('billing_term');
    const display_name = state.display_name ?? null;

    if (!plan_id || !billing_term) {
      navigate('/', { replace: true });
      return;
    }
    if (!isValidPlanId(plan_id) || !isValidBillingTerm(billing_term)) {
      navigate('/', { replace: true });
      return;
    }

    const plan = getPlanById(plan_id);
    let amount_usd = getAmountUsd(plan_id, billing_term);
    const testPricing = paymentsConfig?.paypal_test_pricing;
    if (testPricing?.plan_id === plan_id && testPricing.amount_usd != null) {
      amount_usd = testPricing.amount_usd;
    }
    if (!plan || amount_usd == null) {
      navigate('/', { replace: true });
      return;
    }

    setResolved({
      plan_id,
      billing_term,
      display_name: display_name || plan.name,
      amount_usd,
      plan,
    });
  }, [location.state, searchParams, navigate, paymentsConfig]);

  const createInternalOrder = useCallback(async () => {
    if (!resolved || !user || !API_BASE) return;
    setOrderLoading(true);
    setOrderError('');
    try {
      const token = await user.getIdToken();
      const attribution = getAttributionSnapshot();
      const response = await fetch(`${API_BASE}/api/payments/orders/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: resolved.plan_id,
          billing_term: resolved.billing_term,
          app_origin: window.location.origin,
          attribution,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to create order');
      }
      setOrderId(payload.order_id);
      setPaystackDisplay(payload.paystack || null);
      const displayAmount = payload.display_amount_usd != null ? payload.display_amount_usd : resolved.amount_usd;
      setResolved((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...(payload.access_period_label ? { access_period_label: payload.access_period_label } : {}),
          ...(payload.display_amount_usd != null ? { amount_usd: payload.display_amount_usd } : {}),
        };
      });
      if (!beginCheckoutTracked) {
        trackBeginCheckout({
          planId: resolved.plan_id,
          planName: payload.plan_name || resolved.display_name,
          value: displayAmount,
          currency: 'USD',
          attributionSnapshot: attribution,
        });
        setBeginCheckoutTracked(true);
      }
    } catch (err) {
      setOrderError(err.message || 'Unable to prepare checkout');
    } finally {
      setOrderLoading(false);
    }
  }, [resolved, user, beginCheckoutTracked]);

  useEffect(() => {
    if (resolved && user && API_BASE && !orderId && !orderLoading && !orderError) {
      createInternalOrder();
    }
  }, [resolved, user, orderId, orderLoading, orderError, createInternalOrder]);

  const handlePayPalSuccess = useCallback(() => {
    const qs = orderId ? `?order_id=${encodeURIComponent(orderId)}&provider=paypal` : '';
    navigate(`/payment-success${qs}`, { replace: true });
  }, [navigate, orderId]);

  const handlePaymentError = useCallback((err) => {
    setPaymentError(err?.message || 'Payment failed. Please try again or choose another payment method.');
  }, []);

  if (loading || !user || !resolved) return null;

  const { display_name, amount_usd, billing_term, plan_id } = resolved;
  const accessPeriod =
    resolved.access_period_label ||
    (billing_term === 'monthly' ? '30 days' : '6 months');
  const paystackMerchantEnabled = paymentsConfig?.paystack_checkout_enabled === true;
  const paystackVisible =
    paymentsConfig?.paystack_checkout_visible ?? paystackMerchantEnabled;
  const paypalEnabled = paymentsConfig?.paypal_checkout_enabled !== false;
  const payfastEnabled = paymentsConfig?.payfast_checkout_enabled === true;
  const showPaymentMethods = !configLoading && (paystackVisible || paypalEnabled);
  const emailAddress = user.email || '';

  async function handlePayfastSubmit(e) {
    e.preventDefault();
    if (!resolved || !user) return;
    setPaymentError('');
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_BASE}/api/payments/payfast/init`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: resolved.plan_id,
          billing_term: resolved.billing_term,
          app_origin: window.location.origin,
          attribution: getAttributionSnapshot(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to initialize PayFast checkout');
      }
      if (!payload?.redirect_url) {
        throw new Error('Checkout redirect is unavailable');
      }
      window.location.assign(payload.redirect_url);
    } catch (err) {
      setPaymentError(err.message || 'Unable to start checkout');
    }
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50">
      <div className="w-full md:w-1/2 flex flex-col justify-center px-4 py-8 sm:px-8 sm:py-16 bg-white border-b md:border-b-0 md:border-r border-slate-200">
        <div className="max-w-md mx-auto w-full">
          <img src={cognidoorBanner} alt="Cognidoor Banner" className="mb-4 sm:mb-6 w-full max-w-xs mx-auto" />
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Complete your purchase</h2>
          <p className="text-sm text-slate-600 mb-4">Selected plan: <strong>{display_name}</strong></p>
          <p className="text-sm text-slate-600 mb-1">Access period: <strong>{accessPeriod}</strong></p>
          <div className="text-2xl sm:text-4xl font-bold mb-4 mt-3 text-slate-900">
            ${amount_usd.toFixed(2)} <span className="text-sm sm:text-base font-medium text-slate-500">USD</span>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 pt-3 sm:pt-4 mt-3 sm:mt-4 text-sm">
            <span className="text-slate-600">Plan price</span>
            <span className="font-medium">${amount_usd.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between font-bold text-base sm:text-lg mt-3 sm:mt-4 text-slate-900">
            <span>Total due today</span>
            <span>${amount_usd.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="w-full md:w-1/2 flex flex-col justify-center px-4 py-8 sm:px-8 sm:py-16">
        <div className="max-w-xl mx-auto w-full">
          {emailAddress ? (
            <p className="text-sm text-slate-600 mb-5">
              Paying as <span className="font-medium text-slate-800">{emailAddress}</span>
            </p>
          ) : null}

          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Choose how to pay</h3>

          {orderLoading ? (
            <p className="text-sm text-slate-600 mb-4" role="status">Creating order…</p>
          ) : null}
          {orderError ? (
            <div className="mb-4">
              <p className="text-sm text-red-600 mb-2" role="alert">{orderError}</p>
              <button
                type="button"
                onClick={createInternalOrder}
                className="text-sm font-medium text-blue-700 underline"
              >
                Try again
              </button>
            </div>
          ) : null}

          {configLoading ? <PaymentAccordionSkeleton /> : null}

          {showPaymentMethods ? (
            <div className="mb-4">
              <PaymentMethodAccordion
                paypalEnabled={paypalEnabled}
                paystackVisible={paystackVisible}
                amountUsd={amount_usd}
                paystackQuote={paystackDisplay}
                defaultExpanded="paypal"
                paypalSection={
                  <PayPalPaymentSection
                    apiBaseUrl={API_BASE}
                    idToken={idToken}
                    planId={plan_id}
                    billingTerm={billing_term}
                    appOrigin={window.location.origin}
                    amountUsd={amount_usd}
                    orderId={orderId}
                    orderLoading={orderLoading}
                    onCaptureSuccess={handlePayPalSuccess}
                    onError={handlePaymentError}
                  />
                }
                paystackSection={
                  <PaystackPaymentSection
                    apiBaseUrl={API_BASE}
                    idToken={idToken}
                    orderId={orderId}
                    amountUsd={amount_usd}
                    paystackQuote={paystackDisplay}
                    disabled={orderLoading || !orderId}
                    onError={handlePaymentError}
                  />
                }
              />
            </div>
          ) : null}

          {!configLoading && !paystackVisible && !paypalEnabled ? (
            <p className="text-sm text-red-600 mb-4" role="alert">
              Payment options are temporarily unavailable. Please try again later or contact support.
            </p>
          ) : null}

          {payfastEnabled ? (
            <form onSubmit={handlePayfastSubmit} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
              <button type="submit" className="w-full py-2 sm:py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors text-xs sm:text-base">
                Subscribe with PayFast
              </button>
            </form>
          ) : null}

          {paymentError ? (
            <div className="text-sm text-red-600 mb-3" role="alert">{paymentError}</div>
          ) : null}

          <p className="text-[13px] sm:text-sm text-slate-600 mb-4 leading-relaxed">
            Access is activated automatically after successful payment. If your payment succeeds but access is not activated, contact support from your Cognidoor account.
          </p>

          <p className="text-[13px] sm:text-sm text-slate-600 mb-4">
            Need help?{' '}
            <Link to="/account" className="font-medium text-blue-700 underline underline-offset-2">
              Contact support from your account page
            </Link>
            .
          </p>

          <CheckoutTrustSection paystackVisible={paystackVisible} />
        </div>
      </div>
    </div>
  );
}
